// Unified breakpoint system for the entire application
export const BREAKPOINTS = {
  // XS < 640, S >= 640, M >= 768, L >= 1024, XL >= 1280
  extra_small: 640,
  small: 768,
  medium: 1024,
  large: 1280,
  extra_large: Infinity,
};

// Get the current breakpoint name based on width (or window width if not provided)
export function getBreakpoint(widthOverride) {
  const w = typeof widthOverride === "number" ? widthOverride : window.innerWidth;
  for (const [name, max] of Object.entries(BREAKPOINTS)) {
    if (w < max) return name;
  }
  return "extra_large";
}

// Check if current screen size matches a specific breakpoint
export function isBreakpoint(breakpoint, widthOverride) {
  const current = getBreakpoint(widthOverride);
  return current === breakpoint;
}

// Check if current screen size is smaller than a specific breakpoint
export function isSmallerThan(breakpoint, widthOverride) {
  const width = typeof widthOverride === "number" ? widthOverride : window.innerWidth;
  return width < BREAKPOINTS[breakpoint];
}

// Check if current screen size is larger than a specific breakpoint
export function isLargerThan(breakpoint, widthOverride) {
  const width = typeof widthOverride === "number" ? widthOverride : window.innerWidth;
  return width >= BREAKPOINTS[breakpoint];
}

// Sidebar width classes based on breakpoints
export const SIDEBAR_WIDTH_CLASSES = {
  // Responsive: use a % width, but cap it so it doesn't become overly wide on tablets / larger mobile.
  // (Effectively: width = min(95%, 420px))
  small: "w-[95%] max-w-[420px]",
  medium: "w-[60%]",
  large: "w-1/4",
  // Hidden sidebars must truly collapse to 0 in flex layouts even when their children
  // have intrinsic widths. `basis-0` avoids content-sized flex-basis, and `max-w-0`
  // prevents any max-width remnants from previous states.
  hidden: "w-0 max-w-0 basis-0",
};

// Get appropriate sidebar width class based on screen size and visibility
export function getSidebarWidth(showSidebar, breakpoint) {
  if (!showSidebar) return SIDEBAR_WIDTH_CLASSES.hidden;

  switch (breakpoint) {
    case "extra_small":
    case "small":
      return SIDEBAR_WIDTH_CLASSES.small;
    case "medium":
      return SIDEBAR_WIDTH_CLASSES.medium;
    case "large":
    case "extra_large":
      return SIDEBAR_WIDTH_CLASSES.large;
    default:
      return SIDEBAR_WIDTH_CLASSES.medium;
  }
}

// Default classes for sidebar container
export const DEFAULT_SIDEBAR_CLASSES =
  // Prevent flexbox from shrinking the sidebar width when the center content is `w-full`.
  // Also force min-width:0 so hidden sidebars (`w-0`) truly collapse even when their
  // children have intrinsic widths (otherwise the flex item's default `min-width:auto`
  // can reserve space and make max-width page content look left-shifted).
  "m-0 transition-all duration-300 ease-in-out self-stretch shrink-0 min-w-0";
